import type { BrandKit } from "../models/BrandKit";

export const VISUAL_STYLE_IDS = [
  "auto",
  "brandkit-only",
  "photo",
  "cinematic",
  "clean-illustration",
  "modern-graphic",
] as const;

export type VisualStyleId = (typeof VISUAL_STYLE_IDS)[number];

export interface VisualStyleOption {
  id: VisualStyleId;
  label: string;
  description: string;
  brandKitBehavior: string;
}

export const DEFAULT_VISUAL_STYLE_ID: VisualStyleId = "auto";

export const VISUAL_STYLE_OPTIONS: VisualStyleOption[] = [
  {
    id: "auto",
    label: "Auto",
    description: "AI picks one cohesive look across your post.",
    brandKitBehavior:
      "Reuses source or brand images where they fit. New images follow your Brand Kit's look when it has a clear one.",
  },
  {
    id: "brandkit-only",
    label: "Link & Brand Kit",
    description: "Uses only images from your link and Brand Kit.",
    brandKitBehavior: "No new images are created. Images may repeat across slides.",
  },
  {
    id: "photo",
    label: "Photo",
    description:
      "Realistic social photos of people, products, places, and objects.",
    brandKitBehavior:
      "Reuses source or brand images where they fit. New images use the Photo style with your brand colors.",
  },
  {
    id: "cinematic",
    label: "Cinematic",
    description:
      "Dramatic realistic scenes with strong light, depth, and atmosphere.",
    brandKitBehavior:
      "Reuses source or brand images where they fit. New images use the Cinematic style with your brand colors.",
  },
  {
    id: "clean-illustration",
    label: "Illustration",
    description:
      "Clean editorial drawings with simple forms and clear metaphors.",
    brandKitBehavior:
      "Reuses source or brand images where they fit. New images use the Illustration style with your brand colors.",
  },
  {
    id: "modern-graphic",
    label: "Graphics",
    description:
      "Bold poster-like visuals with shapes, contrast, and abstract metaphors.",
    brandKitBehavior:
      "Reuses source or brand images where they fit. New images use the Graphics style with your brand colors.",
  },
];

type BrandKitImagesBlock = "no-kit" | "no-images" | "processing" | "unusable";

function brandKitImagesBlock(
  brandKit?: BrandKit,
  sourceMayHaveImages = false,
): BrandKitImagesBlock | undefined {
  // Source images are checked by the server after extraction.
  if (sourceMayHaveImages) return undefined;
  if (!brandKit) return "no-kit";
  const images = (brandKit.brandAssets || []).filter(
    (asset) => asset.type === "image",
  );
  if (
    images.some((asset) => asset.processingStatus === "ready" && asset.storagePath)
  )
    return undefined;
  if (!images.length) return "no-images";
  if (
    images.some(
      (asset) => asset.processingStatus === "pending" || asset.processingStatus === "processing",
    )
  )
    return "processing";
  return "unusable";
}

const BLOCK_REASONS: Record<BrandKitImagesBlock, string> = {
  "no-kit":
    "Paste a link with images, or pick a Brand Kit that has images.",
  "no-images":
    "The selected Brand Kit has no images. Add images or choose another image style.",
  processing:
    "Images in the selected Brand Kit are still processing. Wait for them to finish or choose another image style.",
  unusable:
    "The selected Brand Kit has no usable images. Upload another image or choose another image style.",
};


export function brandKitImagesDisabledReason(
  brandKit?: BrandKit,
  sourceMayHaveImages = false,
): string | undefined {
  const block = brandKitImagesBlock(brandKit, sourceMayHaveImages);
  return block && BLOCK_REASONS[block];
}

/** Whether the disabled tile should read as "wait" rather than "empty". */
export function brandKitImagesProcessing(
  brandKit?: BrandKit,
  sourceMayHaveImages = false,
): boolean {
  return brandKitImagesBlock(brandKit, sourceMayHaveImages) === "processing";
}
